import { useState, type CSSProperties, type ImgHTMLAttributes } from 'react';

interface SafeImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /**
   * Optional caption-like fallback shown when the image 404s.
   * Defaults to the alt text. Pass an empty string to suppress.
   */
  fallbackHint?: string;
  /** Optional override for the fallback background. */
  fallbackBackground?: string;
}

/**
 * Image wrapper that absorbs Unsplash / CDN 404s without showing
 * raw alt text on a blown-out white box. On error, swaps the source
 * for a tinted neutral background and overlays the alt or fallback
 * hint in muted small caps.
 *
 * Use everywhere photography ships in front of a customer.
 */
export function SafeImage({
  src,
  alt = '',
  fallbackHint,
  fallbackBackground,
  style,
  ...rest
}: SafeImageProps) {
  const [errored, setErrored] = useState(false);
  const hint = fallbackHint ?? alt;

  if (errored || !src) {
    const fallbackStyle: CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.75rem',
      background: fallbackBackground ?? 'var(--color-background-surface, #F1ECE4)',
      color: 'var(--color-text-muted, #8C7E72)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '0.6875rem',
      fontWeight: 500,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      textAlign: 'center',
      lineHeight: 1.4,
      ...style,
    };
    return (
      <span role="img" aria-label={alt} style={fallbackStyle}>
        {hint || '·'}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setErrored(true)}
      style={style}
      {...rest}
    />
  );
}
